import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
    Box, 
    Button, 
    Typography,
    Paper,
    IconButton,
    Chip,
    Stack,
    LinearProgress,
    Alert,
    CircularProgress,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Group as GroupIcon,
    CloudUpload as CloudUploadIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    Assignment as AssignmentIcon,
    CalendarToday as CalendarIcon,
    Language as LanguageIcon,
    Translate as TranslateIcon
} from '@mui/icons-material';

// Import components
import TrainingDueInfo from '../../TrainingDueInfo';
import TrainingDueUploadActions from '../../TrainingDueUploadActions';
import TrainingDueEmployees from '../../TrainingDueEmployees';
import TrainingDueEmployeeSelectionModal from '../../TrainingDueEmployeeSelectionModal';
import SignatureModalWithAuto from '../../../common/SignatureModalWithAuto';
import TrainingDueTextModal from '../../TrainingDueTextModal';

// Import services and constants
import {
    getTrainingDueById,
    saveEmployeeSignature,
    updateTrainingDue,
    uploadTrainingDueSignatures,
} from '../../../../services/trainingDueDefaultFormService';
import { initializeDB } from '../../../../config/db';
import {
    TRAINING_DUE_FORM_STORE,
    EMPLOYEE_STORE,
    STATUS_SEND,
    STATUS_FINISH,
} from '../../../../config/constants';

// Pulsing upload button style
const pulsingUploadButton = {
    position: 'relative',
    overflow: 'visible',
    animation: 'pulse 2s ease-in-out infinite',
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
        },
        '50%': {
            transform: 'scale(1.05)',
            boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
        },
    },
};

/**
 * Load course name and texts in English/Spanish by fieldTrainingMasterId
 */
async function loadCourseTextAndName(fieldTrainingMasterId) {
    if (!fieldTrainingMasterId) {
        return { courseName: '', textEnglish: '', textSpanish: '' };
    }

    const db = await initializeDB();
    const tx = db.transaction('trainingDueText', 'readonly');
    const store = tx.objectStore('trainingDueText');
    const textRecord = await store.get(fieldTrainingMasterId);

    if (!textRecord) {
        return {
            courseName: '',
            textEnglish: 'No records for this course.',
            textSpanish: 'No records for this course.',
        };
    }

    return {
        courseName: textRecord.course || '',
        textEnglish: textRecord.textEnglish || 'No records for this course.',
        textSpanish: textRecord.textSpanish || 'No records for this course.',
    };
}

/**
 * Format date helper for PHP date objects
 */
const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    // Handle PHP date object format
    if (typeof dateValue === 'object' && dateValue?.date) {
        return new Date(dateValue.date).toLocaleDateString('en-US');
    }
    
    // Handle string or Date
    if (dateValue) {
        return new Date(dateValue).toLocaleDateString('en-US');
    }
    
    return 'N/A';
};

const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Not sent yet';
    
    // Handle PHP date object format
    if (typeof dateValue === 'object' && dateValue?.date) {
        return new Date(dateValue.date).toLocaleString('en-US');
    }
    
    // Handle string or Date
    if (dateValue) {
        return new Date(dateValue).toLocaleString('en-US');
    }
    
    return 'Not sent yet';
};

export default function TrainingDueDefaultForm() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};

    const selectedTrainingDue = state?.selectedTrainingDue || null;
    const trainingDueId = selectedTrainingDue?.id || state?.trainingDueId || null;

    // Local states
    const [trainingDueRecord, setTrainingDueRecord] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [openTextModal, setOpenTextModal] = useState(false);
    const [textContent, setTextContent] = useState('');
    const [openSignModal, setOpenSignModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeSelectionModalOpen, setEmployeeSelectionModalOpen] = useState(false);
    const [lastSentAt, setLastSentAt] = useState('');
    const sigPadRef = useRef(null);

    // Loading state for upload
    const [isUploading, setIsUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    // Statistics
    const [stats, setStats] = useState({
        total: 0,
        signed: 0,
        unsigned: 0,
        percentage: 0
    });

    /**
     * Find employee by full name in EMPLOYEE_STORE
     */
    const findEmployeeByFullName = useCallback(async (fullName) => {
        const db = await initializeDB();
        const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
        const store = tx.objectStore(EMPLOYEE_STORE);
        const allEmployees = await store.getAll();
        return allEmployees.find(e => e.FullName === fullName);
    }, []);

    /**
     * Load TrainingDue data, load course text and update record in storage
     */
    const loadTrainingDueData = useCallback(async (id) => {
        try {
            setLoading(true);
            let record = await getTrainingDueById(id);
            if (!record) {
                console.error('TrainingDue record not found.');
                navigate('/training-due');
                return;
            }

            // If date is not set - set current
            if (!record.date) {
                record.date = new Date().toISOString();
            }

            // Load course text
            const { courseName, textEnglish, textSpanish } = await loadCourseTextAndName(record.fieldTrainingMasterId);
            record.courseName = courseName || record.course || '';
            record.textEnglish = textEnglish;
            record.textSpanish = textSpanish;

            // Initialize signed employees list
            record.signedEmployees = record.signedEmployees || [];
            if (record.fullName) {
                const foundEmp = await findEmployeeByFullName(record.fullName);
                if (foundEmp && !record.signedEmployees.some(e => e.id === foundEmp.id)) {
                    record.signedEmployees.push({ ...foundEmp, signature: null, signedAt: null });
                }
            }

            // Save updated record to TRAINING_DUE_FORM_STORE
            const db = await initializeDB();
            const tx = db.transaction(TRAINING_DUE_FORM_STORE, 'readwrite');
            const store = tx.objectStore(TRAINING_DUE_FORM_STORE);
            await store.put(record);

            // Update state
            setTrainingDueRecord(record);
            setEmployees(record.signedEmployees);
            setLastSentAt(record.lastSentAt || '');

            // Calculate statistics
            const total = record.signedEmployees.length;
            const signed = record.signedEmployees.filter(emp => emp.signature).length;
            const unsigned = total - signed;
            const percentage = total > 0 ? Math.round((signed / total) * 100) : 0;
            
            setStats({ total, signed, unsigned, percentage });
        } catch (error) {
            console.error("Error loading TrainingDue data:", error);
            navigate('/training-due');
        } finally {
            setLoading(false);
        }
    }, [navigate, findEmployeeByFullName]);

    // Load data on component mount
    useEffect(() => {
        if (!trainingDueId) {
            navigate('/training-due');
            return;
        }
        loadTrainingDueData(trainingDueId);
    }, [trainingDueId, navigate, loadTrainingDueData]);

    /**
     * Open modal to view text (English/Spanish)
     */
    const handleOpenTextModal = useCallback((language) => {
        if (!trainingDueRecord) return;
        const content =
            language === 'english'
                ? trainingDueRecord.textEnglish || 'No records for this course.'
                : trainingDueRecord.textSpanish || 'No records for this course.';
        setTextContent(content);
        setOpenTextModal(true);
    }, [trainingDueRecord]);

    /**
     * Open modal for employee signature
     */
    const handleOpenSignModal = useCallback((employeeId) => {
        setSelectedEmployee(employeeId);
        setOpenSignModal(true);
    }, []);

    /**
     * Save employee signature and immediately upload
     */
    const handleSaveSignature = useCallback(async () => {
        if (sigPadRef.current && trainingDueRecord && selectedEmployee) {
            const signatureData = sigPadRef.current.toDataURL();
            try {
                const updatedRecord = await saveEmployeeSignature(
                    trainingDueRecord.id,
                    selectedEmployee,
                    signatureData
                );
                setTrainingDueRecord(updatedRecord);
                setEmployees(updatedRecord.signedEmployees || []);

                // Update statistics
                const total = updatedRecord.signedEmployees.length;
                const signed = updatedRecord.signedEmployees.filter(emp => emp.signature).length;
                const unsigned = total - signed;
                const percentage = total > 0 ? Math.round((signed / total) * 100) : 0;
                setStats({ total, signed, unsigned, percentage });

                // After save - immediately upload with updated record
                handleUploadSignatures(updatedRecord);
            } catch (error) {
                console.error('Error saving signature:', error);
                alert('Failed to save signature.');
            }
            setOpenSignModal(false);
        }
    }, [selectedEmployee, trainingDueRecord]);

    /**
     * Upload signatures to server
     */
    const handleUploadSignatures = useCallback(
        async (record) => {
            if (!record) return;
            setIsUploading(true);

            try {
                const payload = {
                    trainingDueId: record.id,
                    date: record.date || new Date().toISOString(),
                    signature: record.signedEmployees?.[0]?.['signature'],
                    signedAt: record.signedEmployees?.[0]?.['signedAt'],
                    dateDue: record.dateDue,
                    dateThru: record.dateThru,
                    formType: 'default',
                    course: record.course,
                    operatorName: record.fullName,
                    trainerId: (() => {
                        const empId = localStorage.getItem('employeeId');
                        return (empId && empId !== 'null' && empId !== '') ? empId : null;
                    })(),
                    sendBy: localStorage.getItem('userId') || null,
                };

                const response = await uploadTrainingDueSignatures(payload);

                // If saved successfully on backend
                if (response.status === 200) {
                    const sentRecord = {
                        ...record,
                        status: STATUS_SEND,
                        lastSentAt: new Date().toISOString(),
                    };
                    await updateTrainingDue(sentRecord);
                    setTrainingDueRecord(sentRecord);
                    setEmployees(sentRecord.signedEmployees || []);
                    setLastSentAt(sentRecord.lastSentAt);
                    alert('Data sent successfully');
                    navigate('/training-due');
                } else {
                    throw new Error(`Server responded with status ${response.status}`);
                }
            } catch (error) {
                const failRecord = {
                    ...record,
                    status: STATUS_FINISH,
                };
                await updateTrainingDue(failRecord);
                setTrainingDueRecord(failRecord);
                setEmployees(failRecord.signedEmployees || []);
                setLastSentAt(failRecord.lastSentAt);
                alert('Data was not saved but saved locally');
                console.error("Error uploading signatures:", error);
            } finally {
                setIsUploading(false);
            }
        },
        [navigate]
    );

    /**
     * Update employees list via selection modal
     */
    const handleSaveEmployees = useCallback(async (updatedEmployeeIds) => {
        if (!trainingDueRecord) return;
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const existingSigned = trainingDueRecord.signedEmployees || [];
            const newSigned = [];

            for (const empId of updatedEmployeeIds) {
                const existing = existingSigned.find(e => e.id === empId);
                if (existing) {
                    newSigned.push({ ...existing });
                } else {
                    const empData = await store.get(empId);
                    if (empData) {
                        newSigned.push({ ...empData, signature: null, signedAt: null });
                    }
                }
            }

            const updatedRecord = { ...trainingDueRecord, signedEmployees: newSigned };
            if (updatedRecord.status === 'new' && newSigned.length > 0) {
                updatedRecord.status = 'process';
            }

            const updateTx = db.transaction(TRAINING_DUE_FORM_STORE, 'readwrite');
            const updateStore = updateTx.objectStore(TRAINING_DUE_FORM_STORE);
            await updateStore.put(updatedRecord);

            setTrainingDueRecord(updatedRecord);
            setEmployees(newSigned);

            // Update statistics
            const total = newSigned.length;
            const signed = newSigned.filter(emp => emp.signature).length;
            const unsigned = total - signed;
            const percentage = total > 0 ? Math.round((signed / total) * 100) : 0;
            setStats({ total, signed, unsigned, percentage });
        } catch (error) {
            console.error("Error saving updated employees:", error);
        }
    }, [trainingDueRecord]);

    /**
     * Delete employee from list
     */
    const handleDeleteEmployee = useCallback(async (employeeId) => {
        if (!trainingDueRecord) return;
        try {
            const db = await initializeDB();
            const tx = db.transaction(TRAINING_DUE_FORM_STORE, 'readwrite');
            const store = tx.objectStore(TRAINING_DUE_FORM_STORE);
            const updatedEmployees = (trainingDueRecord.signedEmployees || []).filter(e => e.id !== employeeId);
            const updatedRecord = {
                ...trainingDueRecord,
                signedEmployees: updatedEmployees,
            };
            await store.put(updatedRecord);
            setTrainingDueRecord(updatedRecord);
            setEmployees(updatedRecord.signedEmployees);

            // Update statistics
            const total = updatedEmployees.length;
            const signed = updatedEmployees.filter(emp => emp.signature).length;
            const unsigned = total - signed;
            const percentage = total > 0 ? Math.round((signed / total) * 100) : 0;
            setStats({ total, signed, unsigned, percentage });

            alert('Employee deleted successfully');
        } catch (error) {
            console.error("Error deleting employee:", error);
            alert("Failed to delete employee.");
        }
    }, [trainingDueRecord]);

    // If data not loaded yet - show loading indicator
    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!trainingDueRecord) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Training record not found</Alert>
            </Box>
        );
    }

    const selectedEmployeeName =
        employees.find(emp => emp.id === selectedEmployee)?.FullName || 'Unknown';

    // Check: Upload button should be active only if there's at least one signature
    const canUpload = employees.some(emp => emp.signature);

    // Get status color
    const getStatusColor = () => {
        switch(trainingDueRecord.status) {
            case 'process': return 'warning';
            case STATUS_SEND: return 'success';
            case STATUS_FINISH: return 'info';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            bgcolor: 'background.default'
        }}>
            {/* Fixed Header */}
            <Paper 
                elevation={2}
                sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                    borderRadius: 0,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                            <IconButton 
                                onClick={() => navigate('/training-due')}
                                size="large"
                                sx={{ 
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                    }
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {trainingDueRecord.courseName || trainingDueRecord.course || 'Training Detail'}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={trainingDueRecord.status?.toUpperCase() || 'NEW'}
                                        color={getStatusColor()}
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                        {formatDate(trainingDueRecord.date)}
                                    </Typography>
                                    {trainingDueRecord.fullName && (
                                        <Typography variant="caption" color="text.secondary">
                                            • {trainingDueRecord.fullName}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>

                        {/* Upload Button and Actions in Header */}
                        <Stack direction="row" spacing={2} alignItems="center">
                            {/* Language View Buttons */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LanguageIcon />}
                                onClick={() => handleOpenTextModal('english')}
                            >
                                English
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<TranslateIcon />}
                                onClick={() => handleOpenTextModal('spanish')}
                            >
                                Spanish
                            </Button>

                            {/* Upload Button */}
                            {employees.length > 0 && (
                                <Box sx={{ position: 'relative' }}>
                                    <Button
                                        variant="contained"
                                        color={canUpload ? "primary" : "inherit"}
                                        onClick={() => handleUploadSignatures(trainingDueRecord)}
                                        disabled={!canUpload || isUploading}
                                        startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                        sx={canUpload && !isUploading ? pulsingUploadButton : {}}
                                    >
                                        {isUploading ? 'Uploading...' : 'Upload Signatures'}
                                    </Button>
                                    {lastSentAt && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                position: 'absolute',
                                                bottom: -20,
                                                right: 0,
                                                whiteSpace: 'nowrap',
                                                color: 'text.secondary'
                                            }}
                                        >
                                            Last sent: {formatDateTime(lastSentAt)}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Stack>
                    </Stack>

                    {/* Progress Bar */}
                    {employees.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box sx={{ flex: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={stats.percentage} 
                                        sx={{ height: 8, borderRadius: 1 }}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {stats.percentage}%
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>

                {/* Employee Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        Employees
                    </Typography>

                    {employees.length === 0 ? (
                        <Alert severity="info">
                            No employees assigned. Click "Edit Employee List" to add employees.
                        </Alert>
                    ) : (
                        <TrainingDueEmployees
                            employees={employees}
                            recordStatus={trainingDueRecord.status}
                            onDeleteEmployee={handleDeleteEmployee}
                            onOpenSignModal={handleOpenSignModal}
                        />
                    )}
                </Paper>
            </Box>

            {/* Modals */}
            <TrainingDueEmployeeSelectionModal
                open={employeeSelectionModalOpen}
                onClose={() => setEmployeeSelectionModalOpen(false)}
                onSave={handleSaveEmployees}
                existingEmployees={employees.map(e => e.id)}
            />

            <TrainingDueTextModal
                open={openTextModal}
                onClose={() => setOpenTextModal(false)}
                textContent={textContent}
            />

            <SignatureModalWithAuto
                open={openSignModal}
                onClose={() => setOpenSignModal(false)}
                sigPadRef={sigPadRef}
                employeeName={selectedEmployeeName}
                onAccept={handleSaveSignature}
            />
        </Box>
    );
}